import { countriesToRegion } from '@/lists/countriesToRegion';
import { Region } from '@heidi/schema/ml-scribe';

export type PopularSourceHomepage = { homeUrl: string; label: string };

type PopularSourcesRegionKey = 'GLOBAL' | 'US' | 'CA' | 'UK' | 'AU' | 'EU';

/**
 * Unauthenticated users have no saved country or specialty, so we cannot personalize sources the
 * same way as signed-in users. We show a static shortlist of well-known source homepages per
 * region (from geo when available, otherwise GLOBAL).
 */
const POPULAR_SOURCE_HOMEPAGES_BY_REGION: Record<
  PopularSourcesRegionKey,
  readonly PopularSourceHomepage[]
> = {
  GLOBAL: [
    { homeUrl: 'https://www.who.int/', label: 'WHO' },
    { homeUrl: 'https://www.cochrane.org/', label: 'Cochrane' },
    { homeUrl: 'https://www.nejm.org/', label: 'NEJM' },
    { homeUrl: 'https://www.cdc.gov/', label: 'CDC' },
    { homeUrl: 'https://www.ema.europa.eu/', label: 'EMA' },
  ],
  US: [
    { homeUrl: 'https://www.cdc.gov/', label: 'CDC' },
    { homeUrl: 'https://www.nih.gov/', label: 'NIH' },
    { homeUrl: 'https://www.va.gov/', label: 'VA' },
    { homeUrl: 'https://www.nejm.org/', label: 'NEJM' },
    { homeUrl: 'https://www.aafp.org/', label: 'AAFP' },
  ],
  CA: [
    { homeUrl: 'https://www.canada.ca/', label: 'Health Canada' },
    { homeUrl: 'https://www.cfpc.ca/', label: 'CFPC' },
    { homeUrl: 'https://www.cfp.ca/', label: 'CFP' },
    { homeUrl: 'https://www.cmaj.ca/', label: 'CMAJ' },
    { homeUrl: 'https://academic.oup.com/', label: 'OUP' },
  ],
  UK: [
    { homeUrl: 'https://www.nice.org.uk/', label: 'NICE' },
    { homeUrl: 'https://www.bmj.com/', label: 'BMJ' },
    { homeUrl: 'https://www.thelancet.com/', label: 'Lancet' },
    { homeUrl: 'https://www.rcplondon.ac.uk/', label: 'RCP' },
    { homeUrl: 'https://www.rcgp.org.uk/', label: 'RCGP' },
  ],
  AU: [
    { homeUrl: 'https://www.nps.org.au/', label: 'NPS' },
    { homeUrl: 'https://www.nhmrc.gov.au/', label: 'NHMRC' },
    { homeUrl: 'https://www.safetyandquality.gov.au/', label: 'ACSQHC' },
    { homeUrl: 'https://www.health.gov.au/', label: 'DoH' },
    { homeUrl: 'https://www.racgp.org.au/', label: 'RACGP' },
  ],
  EU: [
    { homeUrl: 'https://www.ema.europa.eu/', label: 'EMA' },
    { homeUrl: 'https://www.rki.de/', label: 'RKI' },
    { homeUrl: 'https://www.sbu.se/', label: 'SBU' },
    { homeUrl: 'https://www.esmo.org/', label: 'ESMO' },
    { homeUrl: 'https://www.annalsofoncology.org/', label: 'Ann Oncol' },
  ],
};

function regionToPopularSourcesKey(region: Region): PopularSourcesRegionKey {
  switch (region) {
    case Region.US:
    case Region.US_AZ1:
      return 'US';
    case Region.CA:
      return 'CA';
    case Region.UK:
      return 'UK';
    case Region.AU:
    case Region.AU_AZ1:
      return 'AU';
    case Region.EU:
    case Region.SE_AZ1:
      return 'EU';
    case Region.JP_AZ1:
    case Region.UAE_AZ1:
    case Region.QAT_AZ1:
    default:
      return 'GLOBAL';
  }
}

/**
 * Resolves the popular source homepages to show on the unauthenticated evidence signup card
 * from an English country name (e.g. from {@link getCountryFromGeo}).
 * Uses the GLOBAL list when geo is unavailable, the country is unmapped, or the region has no
 * dedicated list (e.g. JP-AZ1, UAE-AZ1, QAT-AZ1).
 */
export function getPopularSourceHomepagesForGeoCountry(
  countryName: string | null | undefined
): readonly PopularSourceHomepage[] {
  if (!countryName) return POPULAR_SOURCE_HOMEPAGES_BY_REGION.GLOBAL;
  const region = countriesToRegion[countryName];
  if (region === undefined) return POPULAR_SOURCE_HOMEPAGES_BY_REGION.GLOBAL;
  const key = regionToPopularSourcesKey(region);
  return POPULAR_SOURCE_HOMEPAGES_BY_REGION[key];
}
